import { useInternalAuditRating } from '@risksmart-app/components/src/hooks/useRating';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useControlTypeLabel } from 'src/pages/assessments/forms/useControlTypeLabel';

import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { decorateWithControlType, getParentTitle } from './helpers';
import type {
  InternalAuditResultFields,
  InternalAuditResultRegisterFields,
} from './types';

export const useLabelledFields = (
  records: InternalAuditResultFields[] | undefined,
  configIds: string[] = []
) => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const assessmentResultTypes = at('resultTypes', { returnObjects: true });
  const getControlTypeLabel = useControlTypeLabel();
  const { resolveRiskRating } = useRiskRatingResolver('internal_audit', {
    configType: 'internal_audit',
    configIds,
  });
  const { getByValue: getByResultValue } =
    useInternalAuditRating('performance_result');
  const { getByValue: getEffectivenessByValue } =
    useInternalAuditRating('effectiveness');
  const labelledFields = useMemo<
    InternalAuditResultRegisterFields[] | undefined
  >(() => {
    const ratingFns = {
      document_internal_audit_result: (d: InternalAuditResultFields) =>
        getByResultValue(d.Rating),
      obligation_internal_audit_result: (d: InternalAuditResultFields) =>
        getByResultValue(d.Rating),
      control_test_internal_audit_result: (d: InternalAuditResultFields) =>
        getEffectivenessByValue(d.OverallEffectiveness),
      risk_uncontrolled_internal_audit_result: (d: InternalAuditResultFields) =>
        resolveRiskRating({
          likelihood: d.Likelihood,
          impact: d.Impact,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          rating: d.Rating,
          configId: d.ConfigId,
        }),

      risk_controlled_internal_audit_result: (d: InternalAuditResultFields) =>
        resolveRiskRating({
          likelihood: d.Likelihood,
          impact: d.Impact,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          rating: d.Rating,
          configId: d.ConfigId,
        }),
    };

    return records?.map((d) => {
      return {
        ...d,
        TypeLabelled: decorateWithControlType(
          assessmentResultTypes[d.ResultType],
          getControlTypeLabel,
          d
        ),
        ParentTitle: getParentTitle(d) || '-',
        RatingLabelled: ratingFns[d.ResultType](d)?.label || '-',
        Rationale: d.Rationale,
        TestDate: d.TestDate,
      };
    });
  }, [
    records,
    assessmentResultTypes,
    getByResultValue,
    getControlTypeLabel,
    resolveRiskRating,
    getEffectivenessByValue,
  ]);

  return labelledFields;
};
